import React, { useEffect } from "react";
import WS from "src/api/ws";
import { GetAllAccessRuleOptionsResponse } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";

/** Data provided by the {@link ACCESS_RULE_CONTEXT} */
export type AccessRuleContext = {
    /** The currently active context */
    options: GetAllAccessRuleOptionsResponse;

    /** Reload the devices' information */
    reset: () => void;
};

/** {@link React.Context} to access {@link GetAllAccessRuleOptionsResponse} */
const ACCESS_RULE_CONTEXT = React.createContext<AccessRuleContext>({
    options: {
        groups: [],
        tags: [],
        subnets: [],
    },

    /**
     * Reset the context
     */
    reset: () => {},
});
ACCESS_RULE_CONTEXT.displayName = "AccessRuleContext";
export default ACCESS_RULE_CONTEXT;

/**
 * The properties for {@link AccessRuleOptionProvider}
 */
export type AccessRuleOptionProviderProps = {
    /**
     * Children of the provider
     */
    children: React.ReactNode | Array<React.ReactNode>;
};

/**
 * The provider of a device context
 */
export function AccessRuleOptionProvider(props: AccessRuleOptionProviderProps) {
    const [options, setOptions] = React.useState<GetAllAccessRuleOptionsResponse | "loading">("loading");
    let fetching = false;

    /**
     * Fetch the options for the Access Rule list
     */
    const fetchOptions = async () => {
        if (fetching) return;
        fetching = true;

        const res = await ApiInternal.accessRules.getAllForOptions();
        setOptions({ groups: res.groups, subnets: res.subnets, tags: res.tags });

        fetching = false;
    };

    useEffect(() => {
        fetchOptions().then();

        const tagListener = WS.addEventListener("message.TagsChanged", () => {
            fetchOptions().then();
        });

        const groupListener = WS.addEventListener("message.GroupsChanged", () => {
            fetchOptions().then();
        });

        const subnetListener = WS.addEventListener("message.SubnetsChanged", () => {
            fetchOptions().then();
        });

        return () => {
            WS.removeEventListener(tagListener);
            WS.removeEventListener(groupListener);
            WS.removeEventListener(subnetListener);
        };
    }, []);

    if (options === "loading") {
        return <div>Loading ..</div>;
    }

    return (
        <ACCESS_RULE_CONTEXT.Provider
            value={{
                options: options,
                reset: fetchOptions,
            }}
        >
            {props.children}
        </ACCESS_RULE_CONTEXT.Provider>
    );
}
